/* eslint-disable @typescript-eslint/no-explicit-any */
import { RaissoError, errorFactory } from "./responses.js"
import { GenericResponse } from "./controllers/service-models.js"
import { ZodType } from 'zod'
import { Result, R } from '@mobily/ts-belt'
import type { EventsMap } from './events.js'

export function dispatchEvent<T extends keyof EventsMap>(eventName: T & string, detail: UnpackCustomEvent<EventsMap[T]>): void {
  const event = detail
    ? new CustomEvent(eventName, { bubbles: true, cancelable: true, detail })
    : new Event(eventName, { bubbles: true, cancelable: true });
  document.dispatchEvent(event);
}

export function broadcast(channel: string, message: unknown) {
  if (!('BroadcastChannel' in window)) { return; }
  const bc = new BroadcastChannel(channel);
  bc.postMessage(message);
}

export async function apiCall<T>(url: string, type: 'json', method: string, headers?: HeadersInit, body?: BodyInit, mode?: RequestMode): Promise<Result<T, RaissoError>>;
export async function apiCall<T = string>(url: string, type: 'text', method: string, headers?: HeadersInit, body?: BodyInit, mode?: RequestMode): Promise<Result<T, RaissoError>>;
export async function apiCall<T>(url: string, type: 'json' | 'text', method: string, headers?: HeadersInit, body?: BodyInit, mode?: RequestMode) {
  const controller = new AbortController();
  const { signal } = controller
  let request: Request | undefined
  let response: Response | undefined
  try {
    request = new Request(url, {
      method,
      headers: {
        'x-caller': 'web',
        ...headers
      },
      body,
      cache: 'no-cache',
      credentials: 'omit',
      mode,
      signal,
    })
    const timeoutId = window.setTimeout(() => {
      controller.abort(new Error('Timeout'));
    }, 2000);
    response = await fetch(request);
    window.clearTimeout(timeoutId)
    if (!response.ok) {
      const data: GenericResponse = await response.json();
      return R.Error(errorFactory(RaissoError, data, {
        context: {
          request: { url, body: body?.toString() },
          response: { statusText: response.statusText },
        }
      }))
    }
    return R.fromPromise<T | string>(type === 'json' ? response.json() : response.text());
  } catch (e) {
    if (e instanceof Error) {
      return R.Error(errorFactory(RaissoError, e.message, { cause: e, context: {}}))
    }
    return R.Error(errorFactory(
      RaissoError,
      typeof e === 'string' ? e : 'Opps! Qualcosa è andato storto',
      {
        cause: e,
        context: {
          request: { url, body: (await request?.text()) },
          response: response ? { statusText: response.statusText, body: (await response.clone().text()) } : undefined,
        }
      }
    ));
  }
}

export const responseValidation = <T>(validator: ZodType<T>) => (response: T) => {
  const results = validator.safeParse(response)
  if (!results.success) {
    const formatted = results.error.errors.map(issue => `Errore nella proprietà ${issue.path}: ${issue.message}`)
    throw errorFactory(RaissoError, 'Invalid response', { context: { error: formatted } })
  }
  return results.data
}

